import React, { useEffect, useState } from 'react';
import { CircularProgress, Typography, makeStyles } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import Router from 'components/Router/Router';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceDot
} from 'recharts';
import { formatPercent } from 'utils';
import { dummyData, dateConverter, CustomLegend } from '../Utils';
import PastPerformanceMissingFunds from '../Utils/PastPerformanceMissingFund';
import InfoIcon from '@material-ui/icons/Info';
import { Button } from 'components/Base';
import moment from 'moment';
import TextField from '@material-ui/core/TextField';
import ArrowRightAltRoundedIcon from '@material-ui/icons/ArrowRightAltRounded';
import { compose } from 'ramda';
import Store from 'store/store';
import { MetaType } from 'store/modules/Meta/types';
import TooltipPastPerformance from '../Tooltips/TooltipPastPerformance';
import MorningstarCopyright from 'components/MorningstarCopyright/MorningstarCopyright';
import { fullBenchmarkSort } from '../Utils/benchmarks';
import {
  getStartDate,
  maxDate,
  startPeriod
} from 'components/PastPerformance/utils';
import { deleteLocalKeyStorageFuzzy } from 'components/InvestmentAnalyser/utils';

const { connect, selectors, actions } = Store;

const { useParams } = Router;
const useStyles = makeStyles({
  dot: {
    display: 'inline-block',
    width: '5px',
    height: '5px',
    margin: '0 5px',
    borderRadius: '50%',
    backgroundColor: 'black'
  },
  text: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    '& strong': {
      marginRight: '2px'
    }
  },
  errorText: {
    fontSize: '0.8125rem',
    margin: 0,
    color: '#ec6a21',
    lineHeight: '1'
  },
  errorBox: {
    display: 'flex',
    paddingBottom: '1rem',
    alignItems: 'center'
  },
  icon: {
    fill: '#ec6a21',
    width: '1.5rem',
    marginRight: '1rem'
  },
  toggle: {
    transition: 'all 0.3s ease-in-out'
  },
  '@global': {
    '.recharts-surface': {
      overflow: 'visible'
    }
  }
});

interface PastPerformanceProps {
  data: Array<any>;
  isAnimationActive?: boolean;
  height?: number;
  bigLabels?: boolean;
  isForPdf?: boolean;
  hasDateControl?: boolean;
  isAnyPolicyAdded?: boolean;
  errors?: string[];
  limits?: string[];
  benchmarks: Array<MetaType>;
  fixedBenchmarkButton?: any;
  getPastPerformanceData?: Function;
  isLoading?: boolean;
  setData?: Function;
  showOverview?: boolean;
  hideLegend?: boolean;
  hideBenchmarks?: boolean;
  earliestDates: Array<string>;
  footprints?: any;
  initialPerformancePeriod: string;
  updatePerformancePeriod: Function;
  localStorageKey?: string;
}

const Tick = ({ payload: { value }, ...rest }: any) => (
  <text {...rest} className="bar-chart-tick" dy={12}>
    {dateConverter(value)}
  </text>
);

const PastPerformance = ({
  data,
  isAnimationActive = true,
  height = 300,
  bigLabels = false,
  isForPdf = true,
  hasDateControl = true,
  isAnyPolicyAdded = true,
  errors,
  limits,
  benchmarks,
  earliestDates,
  fixedBenchmarkButton,
  getPastPerformanceData,
  isLoading = false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setData = () => {},
  showOverview = true,
  hideLegend = false,
  hideBenchmarks = false,
  initialPerformancePeriod,
  updatePerformancePeriod,
  footprints,
  localStorageKey = ''
}: PastPerformanceProps) => {
  const theme = useTheme() as any;
  const [error, setError] = useState('');
  const [toggleItems, setToggleItems] = useState([] as string[]);
  const [performancePeriod, setPerformancePeriod] = useState(
    initialPerformancePeriod as any
  );
  const [updateBasedOnData, setUpdateBasedOnData] = useState(false);
  const [dates, setDates] = useState([
    data && data.length && data[0].date,
    data && data.length && data[data.length - 1].date
  ]);
  const [startDate, setStartDate] = useState(
    data && data.length && data[0].date
  );
  const [isDataProvided, setIsDataProvided] = useState(data && data.length > 0);
  const [adjustPercentage, setAdjustPercentage] = useState([] as any);
  const [colourRef, setColourRef] = useState({} as any);
  const [keysToChart, setKeysToChart] = useState([] as any);
  const [displayedKeysToChart, setDisplayedKeysToChart] = useState([
    ''
  ] as string[]);
  const [finalNamesAndValues, setFinalNamesAndValues] = useState({} as any);
  const [tickCount, setTickCount] = useState(0);
  const [toggledData, setToggledData] = useState([] as any[]);
  const [activeDot, setActiveDot] = useState(null);

  const formatDate = (date: string) => {
    return moment(date, 'DD/MM/YYYY').format('YYYY-MM-DD');
  };

  const benchmarkNames = benchmarks.map(benchmark => benchmark.name);

  const [editableDates, setEditableDates] = useState([
    formatDate(data && data.length && data[0].date),
    formatDate(data && data.length && data[data.length - 1].date)
  ]);
  const { portfolio } = useParams() as any;
  localStorageKey =
    localStorageKey || `fintegrate-pp-case-${portfolio}-toggles`;
  const performancePeriods = ['3m', '6m', '1y', '3y', '5y', '10y', startPeriod];

  useEffect(() => {
    if (errors && errors.length === 0) setError('');
    if (errors && errors.length > 0) {
      setError(
        `The following funds have been removed from the performance analysis as there is no past performance data available: ${errors.join(
          ', '
        )}`
      );
    }
  }, [errors]);

  const getExcludedPolicies = () => {
    const toggles = JSON.parse(
      localStorage.getItem(localStorageKey) || ('{}' as string)
    );

    return Object.keys(toggles).filter(
      (toggle: string) => !toggles[toggle] && !benchmarkNames.includes(toggle)
    );
  };

  useEffect(() => {
    if (getPastPerformanceData && startDate !== dates[0] && updateBasedOnData) {
      getPastPerformanceData(dates, getExcludedPolicies());
      setStartDate(dates[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dates]);

  const classes = useStyles();

  useEffect(() => {
    const hasData = data && data.length > 0;
    setIsDataProvided(hasData);
    setUpdateBasedOnData(![null, startPeriod].includes(performancePeriod));
    setDates([
      data && data.length && data[0].date,
      data && data.length && data[data.length - 1].date
    ]);
    setEditableDates([
      hasData ? formatDate(data[0].date) : editableDates[0],
      hasData ? formatDate(data[data.length - 1].date) : editableDates[1]
    ]);
    const dataToRender = hasData ? data : dummyData.pastPerformance;
    const percentageMappedData = dataToRender.map((data: any) => {
      const newObj: any = {};
      for (const key in data) {
        if (hideBenchmarks && benchmarkNames.includes(key)) {
          continue;
        }

        if (key !== 'date') {
          newObj[key] = data[key] * 100;
        } else {
          newObj[key] = data[key];
        }
      }
      delete newObj.year; // TODO REMOVE AFTER API CHANGED
      return newObj;
    });

    setAdjustPercentage(percentageMappedData);
    const dataKeys = Object.keys(percentageMappedData[0])
      .filter((key: string) => key !== 'date')
      .sort((a: string, b: string) => {
        return fullBenchmarkSort(benchmarkNames)(a, b);
      });
    setColourRef(
      dataKeys.reduce((acc: any, dk: string, i: number) => {
        acc[dk] = theme.palette.baseColors.charts['charting'][i];
        return acc;
      }, {})
    );
    const localKeysToChart = dataKeys.filter((key: string) => key !== 'date');
    setKeysToChart(localKeysToChart);
    setFinalNamesAndValues(
      percentageMappedData[percentageMappedData.length - 1]
    );
    setTickCount(Math.ceil(percentageMappedData.length / 10));
    setToggledData(percentageMappedData);

    const localStorageToggles = JSON.parse(
      localStorage.getItem(localStorageKey) || ('{}' as string)
    );

    const items = [] as any;
    localKeysToChart.forEach((item: any) => {
      if (item in localStorageToggles) {
        if (!localStorageToggles[item]) {
          items.push(item);
        }
      }
    });
    setTimeout(() => {
      setToggleItems([...items]);
      // eslint-disable-next-line array-callback-return
      setDisplayedKeysToChart([
        // eslint-disable-next-line array-callback-return
        ...localKeysToChart.filter((ktc: any) => {
          if (!items.includes(ktc)) {
            return ktc;
          }
        })
      ]);
      setUpdateBasedOnData(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const getLastEntry = (name: string) => {
    if (finalNamesAndValues && finalNamesAndValues[name]) {
      return finalNamesAndValues[name];
    }

    const match = adjustPercentage
      .slice()
      .reverse()
      .find((ppr: any) => name in ppr);
    return match ? match[name] : null;
  };

  const storeToggle = (item: string, checked: boolean) => {
    const toggleRef = JSON.parse(
      localStorage.getItem(localStorageKey) || ('{}' as string)
    );
    toggleRef[item] = checked;
    localStorage.setItem(localStorageKey, JSON.stringify(toggleRef));
  };

  const isDateValid = (date: any) => {
    return date.isValid() && date <= moment(maxDate, 'DD/MM/YYYY');
  };

  const updatePastPerformancePeriod = (period: any) => {
    setPerformancePeriod(period);
    updatePerformancePeriod(period);
  };

  const updateDates = (newDates: any, manual = true) => {
    let startDate = moment(newDates[0], 'YYYY-MM-DD') as any;
    let endDate = moment(newDates[1], 'YYYY-MM-DD') as any;

    startDate = isDateValid(startDate)
      ? startDate.format('DD/MM/YYYY')
      : dates[0];

    endDate = isDateValid(endDate) ? endDate.format('DD/MM/YYYY') : dates[1];

    if (startDate === dates[0] && endDate === dates[1]) {
      return false;
    }

    if (manual) {
      updatePastPerformancePeriod(null);
    }
    setDates([startDate, endDate]);
    return true;
  };

  const changePerformancePeriod = (period: string) => {
    updatePastPerformancePeriod(period);
    const startDate = getStartDate(period);

    const formattedStartDate = startDate.format('DD/MM/YYYY');

    const endDate = data[data.length - 1].date;

    const editDates = [
      formatDate(isDateValid(startDate) ? formattedStartDate : data[0].date),
      formatDate(endDate)
    ];

    return updateDates(editDates, false);
  };

  const toggleItem = (itemToToggle: string, checked = false) => {
    //logic here means we are now going to be using local
    const items = toggleItems;
    const indexOfItem = items.indexOf(itemToToggle);
    if (indexOfItem === -1) {
      items.push(itemToToggle);
    } else {
      items.splice(indexOfItem, 1);
    }
    storeToggle(itemToToggle, checked);

    if (getPastPerformanceData && !benchmarkNames.includes(itemToToggle)) {
      getPastPerformanceData(
        [getStartDate(performancePeriod).format('DD/MM/YYYY'), dates[1]],
        getExcludedPolicies()
      );
      return;
    }

    setToggleItems([...items]);
    setDisplayedKeysToChart([
      // eslint-disable-next-line array-callback-return
      ...keysToChart.filter((ktc: any) => {
        if (!items.includes(ktc)) {
          return ktc;
        }
      })
    ] as string[]);
  };

  useEffect(() => {
    const cloneData = JSON.parse(JSON.stringify(adjustPercentage));
    const filteredData = cloneData
      .filter((ppItem: any) => {
        toggleItems.forEach(ti => {
          delete ppItem[ti];
        });
        return ppItem;
      })
      .slice(0, cloneData.findIndex((item: any) => item.date === dates[1]) + 1);

    setData({
      data: data.slice(
        0,
        cloneData.findIndex((item: any) => item.date === dates[1]) + 1
      ),
      failed_codes: errors || [],
      limits: limits || []
    });
    setToggledData(filteredData);
    setFinalNamesAndValues(filteredData[filteredData.length - 1]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adjustPercentage, toggleItems, dates]);

  if (!isDataProvided) return <PastPerformanceMissingFunds />;
  if (!isAnyPolicyAdded) return null;

  const [renderKey, setRenderKey] = useState(0);

  useEffect(() => {
    setRenderKey(prevKey => prevKey + 1);
  }, [toggleItems]);

  useEffect(() => {
    if (isForPdf) {
      deleteLocalKeyStorageFuzzy(localStorageKey);
    }
  }, [isForPdf]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-start',
        padding: '1rem',
        position: 'relative'
      }}
    >
      {!isForPdf && isLoading && (
        <div
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            background: 'rgba(255,255,255,0.6)',
            left: 0,
            top: 0,
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <CircularProgress size="100px" />
        </div>
      )}
      {isForPdf && (
        <Typography variant="subtitle2" className={classes.text}>
          <strong>Performance basis:</strong> Total cumulative return
          <span className={classes.dot} />
          <strong>Pricing Spread:</strong> Bid-Bid
          <span className={classes.dot} />
          <strong>Data Frequency:</strong> Daily
          <span className={classes.dot} />
          <strong>Currency:</strong> Pounds Sterling
          <span className={classes.dot} />
          <strong>Date range:</strong> {dates[0]} - {dates[1]}
        </Typography>
      )}
      {error.length !== 0 && (
        <div className={classes.errorBox}>
          <InfoIcon className={classes.icon} />
          <p className={classes.errorText}>{error}</p>
        </div>
      )}
      {limits && limits.length !== 0 && (
        <>
          {limits.map((limit, index) => (
            <div key={index} className={classes.errorBox}>
              <InfoIcon className={classes.icon} />
              <p className={classes.errorText}>{limit}</p>
            </div>
          ))}
        </>
      )}
      {!isForPdf && hasDateControl && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            flexWrap: 'wrap',
            width: '100%'
          }}
        >
          <div style={{ paddingRight: '1rem', marginBottom: '1rem' }}>
            Performance period
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginRight: '2rem',
              marginBottom: '1rem'
            }}
          >
            {performancePeriods.map(period => {
              return (
                <Button
                  key={period}
                  style={{
                    height: '24px',
                    paddingLeft: period === startPeriod ? '12px' : '0',
                    paddingRight: period === startPeriod ? '12px' : '0',
                    minWidth: '45px'
                  }}
                  color={performancePeriod === period ? 'primary' : 'default'}
                  gray={performancePeriod === period ? false : true}
                  onClick={() => changePerformancePeriod(period)}
                >
                  {period}
                </Button>
              );
            })}
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '1rem'
            }}
          >
            <TextField
              type="date"
              value={editableDates[0]}
              inputProps={{
                max: formatDate(maxDate)
              }}
              onChange={e =>
                setEditableDates([e.target.value, editableDates[1]])
              }
              onBlur={() => updateDates(editableDates)}
            />
            <ArrowRightAltRoundedIcon />
            <TextField
              type="date"
              value={editableDates[1]}
              inputProps={{
                max: formatDate(maxDate)
              }}
              onChange={e =>
                setEditableDates([editableDates[0], e.target.value])
              }
              onBlur={() => updateDates(editableDates)}
            />
          </div>

          <div style={{ marginLeft: 'auto', marginBottom: '1rem' }}>
            {fixedBenchmarkButton}
          </div>
        </div>
      )}
      {!isForPdf && !hasDateControl && (
        <div style={{ width: '100%', textAlign: 'right' }}>
          <strong>Performance period:</strong> {dates[0]} - {dates[1]}
        </div>
      )}

      <ResponsiveContainer key={renderKey} width="100%" height={height}>
        <LineChart
          data={toggledData}
          margin={{ top: 15, right: 30, left: 0, bottom: 5 }}
        >
          <XAxis
            dataKey="date"
            axisLine={false}
            interval={tickCount}
            tickLine={true}
            tick={<Tick />}
          />
          <YAxis unit="%" width={40} axisLine={false} tickLine={false} />
          <CartesianGrid vertical={false} strokeDasharray="3 3" />
          {isDataProvided && (
            <Tooltip
              content={
                <TooltipPastPerformance
                  benchmarks={benchmarkNames}
                  activeDot={activeDot}
                />
              }
              formatter={(val: any) => formatPercent(val, true)}
            />
          )}
          {isDataProvided && !hideLegend && (
            <Legend
              wrapperStyle={{ bottom: '-10px' }}
              content={
                <CustomLegend
                  isBig={bigLabels}
                  getLastEntry={getLastEntry}
                  benchmarks={benchmarkNames}
                  toggles={JSON.parse(
                    localStorage.getItem(localStorageKey) || ('{}' as string)
                  )}
                  toggle={toggleItem}
                  isForPdf={isForPdf}
                  earliestDates={earliestDates}
                  getExcludedPolicies={getExcludedPolicies}
                />
              }
              chartHeight={height}
            />
          )}
          {(isForPdf ? displayedKeysToChart : keysToChart).map(
            (dataKey: any) => (
              <Line
                key={dataKey}
                dataKey={dataKey}
                stroke={colourRef[dataKey]}
                strokeWidth={2}
                dot={false}
                connectNulls
                isAnimationActive={isAnimationActive}
                hide={!displayedKeysToChart.includes(dataKey)}
              />
            )
          )}
          {toggledData.map((entry: any, index: number) => {
            const matchedFootprints = Object.keys(footprints || {}).flatMap(
              (key: string) => {
                // Check if the key exists in the footprints object and has valid entries
                if (!footprints[key]) return [];

                return footprints[key]
                  .filter(
                    (date: string) =>
                      date &&
                      moment(date, 'YYYY-MM-DD').isValid() &&
                      moment(date, 'YYYY-MM-DD').format('DD/MM/YYYY') ===
                        entry.date
                  )
                  .map((date: string) => (
                    <ReferenceDot
                      key={`refDot-${index}-${key}-${date}`}
                      x={entry.date}
                      y={entry[key]}
                      r={8}
                      fill={colourRef[key]}
                      stroke="#FFFFFF"
                      strokeWidth="1"
                      isFront
                      onMouseEnter={() => setActiveDot(entry)}
                      onMouseLeave={() => setActiveDot(null)}
                    />
                  ));
              }
            );

            return matchedFootprints.length > 0 ? matchedFootprints : null;
          })}
        </LineChart>
      </ResponsiveContainer>
      <MorningstarCopyright />
      {isForPdf && showOverview ? (
        <Typography
          variant="subtitle2"
          className={classes.text}
          style={{ marginTop: '1rem' }}
        >
          Performance is based on the current fund allocation and doesn't take
          into account any previous fund switches
        </Typography>
      ) : (
        <></>
      )}
    </div>
  );
};

export default compose(
  connect(
    (state: any) => ({
      initialPerformancePeriod:
        selectors.investmentAnalyserSelectors.performancePeriod(state),
      benchmarks: selectors.metaSelectors.benchmarks(state)
    }),
    {
      updatePerformancePeriod:
        actions.investmentAnalyserActions.updatePerformancePeriod
    }
  )
)(PastPerformance);
